import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { marked, Marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  ConversionResult,
  ConversionOptions,
  ConversionError,
  ConversionErrorType,
  ConversionMetadata,
  DEFAULT_CONVERSION_OPTIONS,
  LARGE_DOCUMENT_THRESHOLD,
  ConversionWarningCode
} from '../models/markdown-conversion.model';

@Injectable({ providedIn: 'root' })
export class MarkdownService {
  private localMarked = new Marked({ gfm: true, breaks: true });

  constructor(private sanitizer: DomSanitizer) {}

  convertToHtml(
    markdown: string,
    options?: Partial<ConversionOptions>
  ): Observable<ConversionResult> {
    const startTime = performance.now();
    const mergedOptions: ConversionOptions = { ...DEFAULT_CONVERSION_OPTIONS, ...options };

    if (markdown === null || markdown === undefined) {
      return of(this.createErrorResult(markdown || '', ConversionErrorType.INVALID_INPUT, 'Input is null or undefined', startTime));
    }

    const inputSize = new Blob([markdown]).size;
    if (inputSize > mergedOptions.maxInputSize) {
      return of(this.createErrorResult(markdown, ConversionErrorType.INPUT_TOO_LARGE,
        `Input size (${inputSize} bytes) exceeds maximum allowed size (${mergedOptions.maxInputSize} bytes)`, startTime));
    }

    if (!markdown.trim()) {
      return of({
        html: '',
        originalMarkdown: markdown,
        convertedAt: new Date(),
        success: true,
        warnings: [],
        metadata: this.createMetadata(0, inputSize, 0, startTime)
      });
    }

    try {
      let html = this.localMarked.parse(markdown) as string;
      let purifiedHtml = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                       'ul', 'ol', 'li', 'a', 'img', 'code', 'pre', 'blockquote', 'table',
                       'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'del', 'ins'],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id'],
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
        FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover']
      });

      purifiedHtml = purifiedHtml.replace(/<ul>/g, '<ul class="space-y-2">');

      const outputSize = new Blob([purifiedHtml]).size;
      const warnings = [];

      if (inputSize > LARGE_DOCUMENT_THRESHOLD) {
        warnings.push({
          level: 'warn' as const,
          message: 'Document size is large, conversion may take longer',
          code: ConversionWarningCode.LARGE_DOCUMENT
        });
      }

      return of({
        html: purifiedHtml,
        originalMarkdown: markdown,
        convertedAt: new Date(),
        success: true,
        warnings,
        metadata: this.createMetadata(html.length, inputSize, outputSize, startTime)
      });
    } catch (error) {
      return of(this.createErrorResult(markdown, ConversionErrorType.PARSE_ERROR, 'Failed to parse markdown', startTime, error as Error));
    }
  }

  private createErrorResult(
    markdown: string, errorType: ConversionErrorType, message: string,
    startTime: number, originalError?: Error
  ): ConversionResult {
    const error: ConversionError = { type: errorType, message, details: originalError?.message, originalError };
    return {
      html: '', originalMarkdown: markdown, convertedAt: new Date(), success: false,
      warnings: [], error, metadata: this.createMetadata(0, new Blob([markdown]).size, 0, startTime)
    };
  }

  private createMetadata(htmlLength: number, inputSize: number, outputSize: number, startTime: number): ConversionMetadata {
    return {
      processingTimeMs: Math.round(performance.now() - startTime),
      inputSizeBytes: inputSize,
      outputSizeBytes: outputSize,
      elementCount: { headers: 0, paragraphs: 0, lists: 0, codeBlocks: 0, links: 0, images: 0, tables: 0 },
      detectedFlavor: 'GFM'
    };
  }
}
